import { useState, useRef, useEffect } from "react";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useSelectedCurrencyState } from "../store/currency";
import { useCurrencyConverter } from "../Hooks/useCurrencyConverter";

const CurrencySelector = () => {
	const [currency, setCurrency] = useSelectedCurrencyState();
	const [open, setOpen] = useState(false);
	const ref = useRef(null);

	const { currencyList, rateMap, loading } = useCurrencyConverter();

	useEffect(() => {
		const h = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener("mousedown", h);
		return () => document.removeEventListener("mousedown", h);
	}, []);

	const selected = rateMap[currency?.toUpperCase()];
	const symbol = selected?.symbol || currency;

	const options = currencyList.filter((c) => c.hasRate || c.is_base);

	if (loading && options.length === 0) {
		return (
			<div
				style={{
					height: 36,
					display: "flex",
					alignItems: "center",
					padding: "0 12px",
					background: "#f8fafc",
					borderRadius: 8,
					border: "1px solid #e2e8f0",
					fontSize: 12,
					color: "#94a3b8",
				}}
			>
				…
			</div>
		);
	}

	return (
		<div ref={ref} style={{ position: "relative" }}>
			<button
				onClick={() => setOpen((v) => !v)}
				style={{
					height: 36,
					display: "inline-flex",
					alignItems: "center",
					gap: 6,
					padding: "0 10px",
					background: "#f8fafc",
					border: "1px solid #e2e8f0",
					borderRadius: 8,
					cursor: "pointer",
					fontSize: 12,
					fontWeight: 600,
					color: "#1e293b",
					transition: "all .12s",
					whiteSpace: "nowrap",
				}}
			>
				<span style={{ fontSize: 13 }}>{symbol}</span>
				<span style={{ color: "#64748b" }}>{currency}</span>
				<KeyboardArrowDown style={{ fontSize: 16, color: "#94a3b8" }} />
			</button>

			{open && (
				<div
					style={{
						position: "absolute",
						right: 0,
						top: "110%",
						zIndex: 200,
						background: "#fff",
						border: "1px solid #e2e8f0",
						borderRadius: 10,
						boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
						minWidth: 200,
						overflow: "hidden",
						maxHeight: 300,
						overflowY: "auto",
					}}
				>
					{/* Header */}
					<div
						style={{
							padding: "10px 14px 6px",
							fontSize: 10,
							fontWeight: 700,
							color: "#94a3b8",
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							borderBottom: "1px solid #f1f5f9",
						}}
					>
						Display Currency
					</div>

					{options.map((c) => {
						const isActive = c.currency_code === currency;
						return (
							<button
								key={c.currency_code}
								onClick={() => {
									setCurrency(c.currency_code);
									setOpen(false);
								}}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "9px 14px",
									border: "none",
									background: isActive ? "#eff6ff" : "none",
									cursor: "pointer",
									textAlign: "left",
									borderBottom: "1px solid #f8fafc",
									transition: "background .1s",
								}}
								onMouseEnter={(e) => {
									if (!isActive) e.currentTarget.style.background = "#f8fafc";
								}}
								onMouseLeave={(e) => {
									if (!isActive) e.currentTarget.style.background = "none";
								}}
							>
								<div style={{ display: "flex", alignItems: "center", gap: 9 }}>
									{/* Symbol chip */}
									<div
										style={{
											width: 30,
											height: 30,
											borderRadius: 6,
											background: isActive ? "#1b3b5f" : "#f1f5f9",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
										}}
									>
										<span
											style={{
												fontSize: 11,
												fontWeight: 800,
												color: isActive ? "#fff" : "#475569",
											}}
										>
											{c.symbol?.slice(0, 3)}
										</span>
									</div>
									<div>
										<div
											style={{
												fontSize: 12,
												fontWeight: isActive ? 700 : 600,
												color: isActive ? "#1b3b5f" : "#1e293b",
											}}
										>
											{c.currency_code}
											{c.is_base && (
												<span
													style={{
														marginLeft: 5,
														fontSize: 9,
														background: "#fef9c3",
														color: "#854d0e",
														padding: "1px 5px",
														borderRadius: 4,
														fontWeight: 700,
													}}
												>
													BASE
												</span>
											)}
										</div>
										<div style={{ fontSize: 10, color: "#94a3b8" }}>
											{c.currency_name}
										</div>
									</div>
								</div>
								{/* Rate */}
								<div style={{ textAlign: "right" }}>
									{c.is_base ? (
										<span style={{ fontSize: 10, color: "#94a3b8" }}>1.00</span>
									) : (
										<span
											style={{
												fontSize: 11,
												fontWeight: 600,
												color: isActive ? "#1b3b5f" : "#475569",
											}}
										>
											{c.rate?.toLocaleString(undefined, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 4,
											})}
										</span>
									)}
									<div style={{ fontSize: 9, color: "#94a3b8" }}>per USD</div>
								</div>
							</button>
						);
					})}

					{options.length === 0 && (
						<div
							style={{
								padding: "16px 14px",
								fontSize: 12,
								color: "#94a3b8",
								textAlign: "center",
							}}
						>
							No currencies with rates set.
							<br />
							<span style={{ fontSize: 10 }}>
								Visit Exchange Rates to add rates.
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default CurrencySelector;
